import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import { LinkContainer } from "react-router-bootstrap";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/esm/Stack";
import Spinner from "react-bootstrap/Spinner";
import { Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faThumbTack } from "@fortawesome/free-solid-svg-icons";

import { AppLayout } from "../components/appLayout";
import useApiClient from "../hooks/useApiClient";
import { CategorySelect, CountType } from "../components/inputs/categorySelect";
import { CategoryWithCountsViewModel, CreateVideoBindingModel, CreateVideosBindingModel } from "../apiClient";
import { getErrorMessage } from "../helpers/errorHelper";
import { getAddVideoBookmarkletHref } from "../components/forms/videoAddEditForm";

export const VideoBulkAdd = () => {
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialUrl = searchParams.get('url') ?? '';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | Array<{ url: string; reason: string }> | null>(null);
    const [categories, setCategories] = useState<CategoryWithCountsViewModel[]>([]);

    useEffect(() => {
        if (!apiClient) return;

        const loadData = async () => {
            const categories = await apiClient.categoriesAll();
            setCategories(categories);
            setLoading(false);
        };

        loadData();
    }, [apiClient]);

    const formik = useFormik({
        initialValues: {
            categoryName: '',
            videoUrls: initialUrl,
        },
        onSubmit: async (values, helpers) => {
            setError(null);
            if (!apiClient) return;

            try {
                const videoUrls = Array.from(new Set(values.videoUrls.split('\n').map(x => x.trim()).filter(x => x)));
                const categoryName = values.categoryName !== '' ? values.categoryName : null;
                const result = await apiClient.videosPOST(new CreateVideosBindingModel({
                    videos: videoUrls.map(url => new CreateVideoBindingModel({
                        categoryName: categoryName,
                        url: url
                    }))
                }));

                if (result.failedVideos && result.failedVideos.length > 0) {
                    setError(result.failedVideos.map(fv => ({
                        url: fv.url ?? '',
                        reason: fv.reason ?? 'Unknown reason'
                    })));
                    const createdUrls = result.addedVideos?.map(v => v.url) ?? [];
                    const notCreatedUrls = videoUrls.filter(url => !createdUrls.includes(url));
                    await helpers.setFieldValue('videoUrls', notCreatedUrls.join('\n'));
                } else {
                    navigate('/');
                }
            } catch (e) {
                console.error(e);
                setError(getErrorMessage(e));
            } finally {
                helpers.setSubmitting(false);
            }
        }
    });

    const urlCount = formik.values.videoUrls
        .split('\n')
        .map(x => x.trim())
        .filter(Boolean).length;

    return (
        <AppLayout loading={loading}>
            <Container className="py-2">
                <Breadcrumb>
                    <LinkContainer to="/">
                        <Breadcrumb.Item>Videos</Breadcrumb.Item>
                    </LinkContainer>
                    <Breadcrumb.Item active>Bulk add videos</Breadcrumb.Item>
                </Breadcrumb>

                <div className="mb-3">
                    <h1 className="mb-1">Bulk add videos</h1>
                    <p className="text-body-secondary mb-0">
                        Paste multiple video URLs (one per line) to import them in a batch.
                    </p>
                </div>

                <Card>
                    <Card.Body className="p-4">
                        <Form onSubmit={formik.handleSubmit}>
                            <fieldset disabled={formik.isSubmitting}>
                                <Stack gap={3}>
                                    {error && (
                                        <Alert variant="danger" className="shadow-sm border-start border-4 border-danger">
                                            <h5 className="alert-heading fw-bold mb-2">Failed to add some videos</h5>
                                            {Array.isArray(error) ? (
                                                <ul className="mb-0 ps-3">
                                                    {error.map((err, index) => (
                                                        <li key={index} className="mb-1">
                                                            <strong className="text-break">{err.url}</strong>: <span className="text-muted">{err.reason}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div style={{ whiteSpace: 'pre-wrap' }}>{error}</div>
                                            )}
                                        </Alert>
                                    )}

                                    <Form.Group controlId="bulk-video-urls">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <Form.Label className="mb-0">Video URLs (one per line)</Form.Label>
                                            {urlCount > 0 && (
                                                <Badge bg="secondary-subtle" className="text-secondary-emphasis">
                                                    {urlCount} {urlCount === 1 ? 'URL' : 'URLs'}
                                                </Badge>
                                            )}
                                        </div>
                                        <Form.Control
                                            as="textarea"
                                            rows={10}
                                            name="videoUrls"
                                            placeholder={"https://www.youtube.com/watch?v=...\nhttps://vimeo.com/..."}
                                            value={formik.values.videoUrls}
                                            onChange={formik.handleChange}
                                            required={true}
                                        />
                                    </Form.Group>

                                    <Form.Group>
                                        <CategorySelect
                                            categories={categories}
                                            name="categoryName"
                                            value={formik.values.categoryName}
                                            onChange={val => formik.setFieldValue('categoryName', val)}
                                            countType={CountType.Videos}
                                        />
                                    </Form.Group>

                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 pt-2">
                                        <Stack direction="horizontal" gap={2}>
                                            <Button variant="primary" type="submit" disabled={!formik.isValid || formik.isSubmitting}>
                                                {formik.isSubmitting ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                                                        Add videos
                                                    </>
                                                )}
                                            </Button>
                                            <Button variant="secondary" onClick={() => navigate('/')}>
                                                Cancel
                                            </Button>
                                        </Stack>

                                        <div className="d-flex align-items-center gap-2 small text-body-secondary">
                                            <span>Drag to bookmarks bar:</span>
                                            <a
                                                ref={(el) => {
                                                    if (el) el.setAttribute('href', getAddVideoBookmarkletHref());
                                                }}
                                                href="#bookmarklet"
                                                onClick={(e) => e.preventDefault()}
                                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                                                title="Drag this link to your browser's bookmarks bar to quickly add any video page"
                                            >
                                                <FontAwesomeIcon icon={faThumbTack} size="xs" />
                                                <span>+ Add Video</span>
                                            </a>
                                        </div>
                                    </div>
                                </Stack>
                            </fieldset>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </AppLayout>
    );
};